import { Capacitor } from "@capacitor/core";
import { api } from "@/shared/api/client";
import { alertIdFromPushData, setPushFocus } from "@/shared/lib/pushFocus";

/**
 * 네이티브(안드로이드/iOS) 푸시 등록 — 백엔드 푸시 루프의 마지막 반쪽.
 *
 * 백엔드(`safety/services/notify/push.py`)는 이미 `NotificationPreference.devices`
 * 의 `{token, platform, label}` 목록으로 FCM/APNs 를 쏜다. 지금까지 그 목록에
 * 토큰을 넣는 주체가 없어 푸시가 끝단에서 끊겨 있었다. 이 모듈이 네이티브에서
 * FCM/APNs 토큰을 받아 그 목록에 등록해 루프를 닫는다.
 *
 * 웹(PWA 브라우저)에서는 `isNativePlatform()` 이 false 라 전부 no-op — 웹 번들은
 * 영향 없고, 실제 네이티브 플러그인 코드는 동적 import 라 웹에 섞여 들어가지 않는다.
 */

interface DeviceToken {
  token: string;
  platform: string;
  label?: string;
}

/**
 * 푸시 활성 여부 — **기본 꺼짐**.
 *
 * ⚠️ Android 에서 `PushNotifications.register()` 는 google-services.json 이 없으면
 * `IllegalStateException: Default FirebaseApp is not initialized` 를 **네이티브
 * 스레드에서** 던진다. 이건 JS try/catch 로 못 잡고 앱이 그대로 죽는다
 * (실측: 로그인 직후 FATAL EXCEPTION → 프로세스 종료).
 *
 * 그래서 자격이 갖춰졌을 때만 켠다. 켜는 조건:
 *   · Android — `android/app/google-services.json` 배치
 *   · iOS     — APNs 키 + Xcode Push Notifications capability
 * 둘 다 준비되면 빌드에 `VITE_ENABLE_PUSH=1` 을 준다.
 *
 * 꺼져 있어도 인앱 WebSocket 경보는 그대로 동작한다 — 잃는 것은 백그라운드
 * 알림뿐이고, 얻는 것은 "앱이 죽지 않는다"이다.
 */
// 2026-10-06(사용자 결정): 안드로이드는 기본으로 켠다 — google-services.json 을 로컬에 두고(FCM 실기기 검증 09-11),
// 직접 설치 빌드(npm run release)에서 꺼져 있어 노트20·보드가 백그라운드 경보를 못 받았다.
// 2026-10-14(앱스토어 등록): 아이폰도 기본으로 켠다 — AppDelegate 가 FCM 토큰을 넘기고(Firebase iOS SDK),
// GoogleService-Info.plist 가 없으면 Firebase 를 초기화하지 않아 토큰만 안 나올 뿐 앱은 죽지 않는다.
// "0" 이면 어디서든 끈다(비상용).
const PUSH_FLAG = import.meta.env.VITE_ENABLE_PUSH;
const PUSH_PLATFORM = Capacitor.getPlatform();
const PUSH_ENABLED =
  PUSH_FLAG === "0" ? false : PUSH_PLATFORM === "android" || PUSH_PLATFORM === "ios" ? true : PUSH_FLAG === "1";

/**
 * 경보 알림 채널은 **네이티브가 만든다** — `android/…/AlertChannels.java`
 * (MainActivity.onCreate). 여기서 만들지 않는 이유:
 *
 * ⛔ Android 8+ 에서 소리·진동·헤드업은 「메시지」가 아니라 「채널」의 속성이고,
 *    Capacitor `createChannel` 은 소리를 `USAGE_NOTIFICATION` 으로만 싣는다 —
 *    진동 모드·알림 음량 0 이면 **가스 경보도 같이 조용해진다.** 경계·심각은
 *    알람 스트림(`USAGE_ALARM`)으로 울려야 하고, 그건 Java 에서만 지정할 수 있다.
 *    또 JS 는 로그인·권한 뒤에야 도는데 채널은 앱이 뜨는 순간 있어야 한다.
 *
 * 채널 id 는 서버 `push.py`·매니페스트 `default_notification_channel_id` 와
 * **글자 그대로** 같아야 한다 — 다르면 FCM 이 에러 없이 조용히 폴백 채널로 내려간다.
 *   · `gasm_alert_critical_v2` — 경계·심각, 커스텀 경보음(`res/raw/gasm_alarm.wav`)
 *   · `gasm_alert_v1`          — 주의, 기본 알림음
 */

export async function initNativePush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!PUSH_ENABLED) return;

  const { PushNotifications } = await import("@capacitor/push-notifications");

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== "granted") return;

  await PushNotifications.removeAllListeners();

  // FCM 토큰 수신 → 서버 등록. iOS 도 AppDelegate 가 APNs 토큰을 FCM 토큰으로 바꿔 넘긴다
  // (APNs 원시 토큰이 서버에 가면 FCM v1 이 400 으로 거부한다).
  PushNotifications.addListener("registration", (token) => {
    void registerDeviceToken(token.value, Capacitor.getPlatform());
  });
  PushNotifications.addListener("registrationError", () => {
    /* 토큰 등록 실패로 앱을 막지 않는다 — 인앱 WebSocket 경보는 계속 동작 */
  });
  // 알림 탭 → 그 경보로(2026-10-19, 10-12 검증 V-22).
  // ⛔ 경보 알림이면 예전처럼 `location.assign` 으로 앱을 통째로 다시 띄우지 않는다 — 다시 뜬 화면이 열린 경보를
  //    기준선에 흡수해 정작 그 경보의 경보창·다국어 음성이 나오지 않았다. 경보 id 를 맡기기만 하면 AlarmCenter 가
  //    라우터로 `/alerts?id=` 로 옮기고, 경보창·낭독기가 그 경보를 기준선 예외로 다시 띄운다(lib/pushFocus.ts).
  //    콜드 스타트에서는 이 리스너가 로그인 뒤에 붙지만 플러그인이 탭 이벤트를 붙들어 두었다가 넘긴다.
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const id = alertIdFromPushData(action?.notification?.data);
    if (id != null) {
      setPushFocus(id);
      return;
    }
    // 경보 id 가 없는 알림(공지 등)은 종전대로 경보 화면으로만.
    try {
      if (location.pathname !== "/alerts") location.assign("/alerts");
    } catch {
      /* noop */
    }
  });

  try {
    await PushNotifications.register();
  } catch {
    /* JS 로 전달되는 실패만 여기서 삼킨다. 네이티브 FATAL 은 위 게이트로 막는다. */
  }
}

/** 이 기기의 푸시 토큰 — 로그아웃 때 서버 목록에서 빼려고 기억한다(앱 재실행 뒤에도). */
const TOKEN_KEY = "expertgas.push.token";

function rememberToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* 저장 불가(사생활 모드 등) — 로그아웃 정리만 못 할 뿐 */
  }
}

function rememberedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * 로그아웃 직전에 이 기기 토큰을 지금 계정의 단말 목록에서 뺀다(2026-10-14).
 *
 * 빼지 않으면 한 폰에서 계정을 바꿔도 앞 계정의 경보가 계속 온다 — 다른 조직 계정이면 정보가 새고,
 * 앱 심사 기기도 심사 계정 경보를 계속 받는다. 다음 로그인 때 initNativePush 가 새 계정에 다시 등록한다.
 * best-effort: 서버가 늦거나 실패해도 로그아웃을 막지 않는다(최대 3초).
 */
export async function unregisterDeviceToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  const token = rememberedToken();
  if (!token) return;
  const work = (async () => {
    const pref = (await api.get<{ devices?: DeviceToken[] }>("/notifications/preferences/")).data;
    const devices: DeviceToken[] = Array.isArray(pref.devices) ? pref.devices : [];
    const next = devices.filter((d) => d?.token !== token);
    if (next.length !== devices.length) {
      await api.put("/notifications/preferences/me/", { devices: next });
    }
  })();
  try {
    await Promise.race([work, new Promise((resolve) => setTimeout(resolve, 3000))]);
  } catch {
    /* 정리 실패로 로그아웃을 막지 않는다 */
  }
}

/** 현재 devices 목록을 읽어 이 토큰을 dedupe 추가한 뒤 PUT. push_enabled 도 켠다. */
async function registerDeviceToken(token: string, platform: string): Promise<void> {
  rememberToken(token);
  try {
    const pref = (await api.get<{ devices?: DeviceToken[] }>("/notifications/preferences/")).data;
    const devices: DeviceToken[] = Array.isArray(pref.devices) ? pref.devices : [];
    if (devices.some((d) => d?.token === token)) return; // 이미 등록됨
    const next = [...devices, { token, platform, label: `${platform} app` }];
    await api.put("/notifications/preferences/me/", { devices: next, push_enabled: true });
  } catch {
    /* 서버 등록 실패는 조용히 넘긴다 — 다음 실행 시 재시도된다 */
  }
}
